import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowBack } from '@mui/icons-material';
import { Alert, Box, Button, Stack } from '@mui/material';
import { formatFirebaseAuthError } from '../../utils/errorHandling';
import AuthLayout from './AuthLayout';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { ForgotPasswordFormData, forgotPasswordSchema } from '../../utils/validation/auth.schema';
import FormTextField from './FormTextField';

const ForgotPasswordPage = () => {
    const { resetPassword } = useAuth();
    const { t } = useTranslation();
    const [errorKey, setErrorKey] = useState<string | null>(null);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const {
        control,
        handleSubmit: formSubmit,
        formState: { isSubmitting },
        reset,
    } = useForm<ForgotPasswordFormData>({
        resolver: zodResolver(forgotPasswordSchema),
        defaultValues: {
            email: '',
        },
        mode: 'onBlur',
    });

    const handleSubmit = async (data: ForgotPasswordFormData) => {
        try {
            setMessage('');
            setErrorKey(null);
            setLoading(true);
            await resetPassword(data.email);
            setMessage(t('auth.forgotPassword.checkEmail'));
            reset(); // Clear the form after success
        } catch (err: any) {
            setErrorKey(formatFirebaseAuthError(err));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const subtitleElement = (
        <>{t('auth.forgotPassword.description')}</>
    );

    return (
        <AuthLayout 
            title={t('auth.forgotPassword.title')}
            subtitle={subtitleElement}
        >
                
                {errorKey && (
                    <Alert 
                        severity="error" 
                        sx={{
                            marginBottom: '24px',
                            borderRadius: '8px',
                        }} 
                        role="alert" 
                        aria-live="assertive"
                    >
                        {t(errorKey)}
                    </Alert>
                )}
                {message && (
                    <Alert 
                        severity="success" 
                        sx={{
                            marginBottom: '24px',
                            borderRadius: '8px',
                        }} 
                        role="status" 
                        aria-live="polite"
                    >
                        {message}
                    </Alert>
                )}

                {/* Only show the form if there's no success message */}
                {!message && (
                    <Box 
                        component="form" 
                        onSubmit={formSubmit(handleSubmit)} 
                        sx={{ width: '100%', marginTop: '16px' }} 
                        noValidate
                    >
                    <Stack spacing={2}>

                        <FormTextField
                            required
                            fullWidth
                            label={t('auth.email')}
                            type="email"
                            autoComplete="email"
                            name="email"
                            control={control}
                            inputProps={{
                                'aria-label': 'Email address',
                            }}
                            disabled={isSubmitting || loading}
                            sx={{
                                mb: 2
                            }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={isSubmitting || loading}
                            sx={{
                                marginTop: '24px',
                                fontWeight: 600,
                                textTransform: 'none',
                                fontSize: '1rem',
                                backgroundColor: '#FF6B00',
                                color: '#ffffff',
                                height: '48px',
                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                                '&:hover': {
                                    backgroundColor: '#FF7E33',
                                    boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.25)',
                                },
                            }}
                            aria-label="Reset Password"
                        >
                            {t('auth.forgotPassword.submitButton')}
                        </Button>
                    </Stack>
                </Box>
                )}
            <Box sx={{
                marginTop: '32px',
                textAlign: 'center',
            }}>
                <Button
                    component={RouterLink}
                    to="/login"
                    variant="text"
                    startIcon={<ArrowBack />}
                    sx={{
                        color: '#FF6B00',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 107, 0, 0.08)',
                        },
                    }}
                    aria-label="Back to Login"
                >
                    {t('auth.forgotPassword.backToLogin')}
                </Button>
            </Box>
            </AuthLayout>
    );
};

export default ForgotPasswordPage;
